import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { TippyDirective } from '@/directives/tippy.directive';
import { LazyLoadImageModule } from 'ng-lazyload-image';

import { WalletAddressDirective } from '@/directives/wallet-address.directive';

import { WeiToEthPipe } from '@/pipes/wei-to-eth.pipe';
import { FormatCashPipe } from '@/pipes/format-cash.pipe';
import { CamelCase2TitleCase } from '@/pipes/cc2tc.pipe';
import { UsdPipe } from '@/pipes/usd.pipe';
import { MarketplaceIconsComponent } from '@/components/marketplace-icons/marketplace-icons.component';
import { LoaderComponent } from '@/components/icons/loader.component';

import { DataService } from '@/services/data.service';

import { BehaviorSubject, Observable, Subject, catchError, forkJoin, map, of, switchMap, takeUntil, tap, timer, withLatestFrom } from 'rxjs';

import { Apollo, gql } from 'apollo-angular';
import { ApolloQueryResult } from '@apollo/client';

import { Event } from '@/models/graph';

import { ZERO_ADDRESS, TOKENWORKS_ADDRESS, PUNKAUCTION_ADDRESS } from '@/constants/utils';
import { isPunksmarketNativeSale } from '@/constants/punksmarket-v1';
import { environment } from 'src/environments/environment';
import { GraphQLQueriesService } from '@/services/graphql-queries.service';
import { PlatformService } from '@/services/platform.service';

// TODO readd platform here when new graph released
const GET_DATA = gql`
query GetEvents($limit: Int!, $skip: Int!, $tokenId: String!) {
  events(
    first: $limit,
    skip: $skip,
    where: {
      tokenId: $tokenId
    },
    orderBy: blockTimestamp,
    orderDirection: desc
  ) {
    type
    platform
    value
    usd
    transactionHash
    blockTimestamp
    fromAccount {
      id
    }
    toAccount {
      id
    }
  }
}
`;

@Component({
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    RouterModule,
    LazyLoadImageModule,
    TippyDirective,
    WalletAddressDirective,
    WeiToEthPipe,
    FormatCashPipe,
    CamelCase2TitleCase,
    UsdPipe,
    MarketplaceIconsComponent,
    LoaderComponent
  ],
  selector: 'app-tx-history',
  templateUrl: './tx-history.component.html',
  styleUrls: ['./tx-history.component.scss']
})
export class TxHistoryComponent implements OnDestroy, OnChanges, OnInit {
  ZERO_ADDRESS = ZERO_ADDRESS;
  TOKENWORKS_ADDRESS = TOKENWORKS_ADDRESS;
  PUNKAUCTION_ADDRESS = PUNKAUCTION_ADDRESS;

  @Input() tokenId!: string;

  private destroy$ = new Subject<void>();

  private tokenSales = new BehaviorSubject<Event[] | null>(null);
  tokenSales$ = this.tokenSales.asObservable();

  private activeTokenId = new BehaviorSubject<string | undefined>(undefined);
  environment = environment;

  // Pagination state
  limit = 30;
  skip = 0;
  hasMore = true;
  isLoading = false;

  // Sales only filter
  salesOnly = false;
  private readonly STORAGE_KEY = 'tx-history-sales-only';

  /** Rich HTML for V2Airdrop info tooltip (Tippy allowHTML) */
  readonly v2AirdropTooltipContent = `
<div class="v2-airdrop-tooltip">
  <p class="v2-airdrop-tooltip__title">V2 airdrop</p>
  <p class="v2-airdrop-tooltip__lede">When CryptoPunks launched in 2017, the original smart contract contained a coding error that allowed buyers to withdraw the ETH they'd just paid, letting them keep both the Punk and their money while the seller was left with nothing.</p>
  <p class="v2-airdrop-tooltip__body">Larva Labs deployed a V2 contract and airdropped new CryptoPunks. Being immutable, the V1 contract and CryptoPunks claimed from it, remain on the blockchain.</p>
</div>`.trim();

  private readonly V2_PUNKS_CONTRACT = '0xb47e3cd837ddf8e4c57f05d70ab865de6e193bbb';

  constructor(
      private apollo: Apollo,
      private http: HttpClient,
      public dataSvc: DataService,
      private graphqlService: GraphQLQueriesService,
      private platformService: PlatformService
  ) {}

  ngOnInit(): void {
    // Restore salesOnly state from localStorage
    this.restoreSalesOnlyState();
    
    // If tokenId is already set, trigger initial load with restored filter state
    if (this.tokenId && !this.activeTokenId.value) {
      this.skip = 0;
      this.hasMore = true;
      this.tokenSales.next([]);
      this.activeTokenId.next(this.tokenId);
      this.fetchEvents(false);
    }
  }

  private restoreSalesOnlyState(): void {
    if (this.platformService.isClient) {
      const savedState = this.platformService.getLocalStorageItem(this.STORAGE_KEY);
      if (savedState === 'true') {
        this.salesOnly = true;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Restore state before loading data to ensure filter is applied
    this.restoreSalesOnlyState();

    if (changes.tokenId?.currentValue) {
      console.log('[TxHistoryComponent] ngOnChanges', changes.tokenId.currentValue);
      this.skip = 0;
      this.hasMore = true;
      this.tokenSales.next([]);
      this.activeTokenId.next(changes.tokenId.currentValue);
      this.fetchEvents(false);
    }
  }

  toggleSalesOnly(): void {
    this.salesOnly = !this.salesOnly;
    // Save state to localStorage
    this.platformService.setLocalStorageItem(this.STORAGE_KEY, String(this.salesOnly));
    this.skip = 0;
    this.hasMore = true;
    this.tokenSales.next([]);
    this.fetchEvents(false);
  }

  fetchEvents(append = false): void {
    console.log('[TxHistoryComponent] fetchEvents', append);
    const tokenId = this.activeTokenId.value;
    if (!tokenId || this.isLoading || !this.hasMore) return;

    this.isLoading = true;

    // Feature flag to switch between old and new approach
    const useGraphQLService = environment.featureFlags?.NEW_GRAPH_FOR_TX_HISTORY || false;

    if (useGraphQLService) {
      // Use GraphQL service with alternative endpoint
      const query = this.graphqlService.getQuery(environment.collection, 'GET_TX_HISTORY');
      const mapper = this.graphqlService.getMapper(environment.collection, 'GET_TX_HISTORY');
      
      if (!query) {
        console.error('[TxHistoryComponent] GET_TX_HISTORY query not found');
        this.isLoading = false;
        return;
      }

      const mainQuery$ = this.apollo.use('alternative').query<{ getAllEvents: Event[] }>({
        query: query,
        variables: {
          tokenId: tokenId,
          collections: [environment.collection === 'cryptopunks' ? this.V2_PUNKS_CONTRACT : environment.collection, environment.wrappedPunksAddress, environment.c721Address],
          eventType: this.salesOnly ? 'sale' : undefined
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      }).pipe(
        map((res: ApolloQueryResult<{ getAllEvents: Event[] }>) => {
          const events = res.data?.getAllEvents || [];
          return mapper ? mapper({ getAllEvents: events }) : events;
        }),
        withLatestFrom(this.dataSvc.usd$),
        map(([res, usd]) => {
          return res.map((event: Event) => {
            const newEvent = { ...event };
            if (newEvent.usd === '1815') newEvent.usd = `${Math.floor(usd)}`;
            return newEvent;
          });
        }),
        catchError((err) => {
          console.error(err);
          return of([]);
        })
      );

      const v2Airdrop$ = this.fetchCrossCollectionEvent(tokenId);

      // Ensure loading is visible for at least 200ms
      forkJoin({
        data: forkJoin([mainQuery$, v2Airdrop$]).pipe(
          map(([main, airdrop]) => {
            const merged = [...main, ...airdrop];
            merged.sort((a, b) => +b.blockTimestamp - +a.blockTimestamp);
            return merged;
          })
        ),
        timer: timer(1000)
      }).pipe(
        map(({ data }) => data),
        tap((events) => {
          // For getAllEvents, we get all events at once, so no pagination needed
          this.hasMore = false;
          this.skip = events.length;

          const current = this.tokenSales.getValue() ?? [];
          this.tokenSales.next(append ? [...current, ...events] : events);
        }),
        tap(() => {
          this.isLoading = false;
        }),
        takeUntil(this.destroy$)
      ).subscribe();
    } else {
      // Use old approach
      const queryObservable = this.apollo.query<{ events: Event[] }>({
        query: GET_DATA,
        variables: {
          limit: this.limit,
          skip: this.skip,
          tokenId: tokenId
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      }).pipe(
          map((res: ApolloQueryResult<{ events: Event[] }>) => {
            let events = res.data?.events || [];
            // Filter by sales only if toggle is enabled
            if (this.salesOnly) {
              events = events.filter(event => event.type?.toLowerCase() === 'sale');
            }
            return events;
          }),
          withLatestFrom(this.dataSvc.usd$),
          map(([res, usd]) => {
            return res.map((event) => {
              const newEvent = { ...event };
              if (newEvent.usd === '1815') newEvent.usd = `${Math.floor(usd)}`;
              return newEvent;
            });
          }),
          catchError((err) => {
            console.error(err);
            return of([]);
          })
      );

      // Ensure loading is visible for at least 200ms
      forkJoin({
        data: queryObservable,
        timer: timer(200)
      }).pipe(
        map(({ data }) => data),
        tap((events) => {
          if (events.length < this.limit) {
            this.hasMore = false;
          }
          this.skip += events.length;

          const current = this.tokenSales.getValue() ?? [];
          this.tokenSales.next(append ? [...current, ...events] : events);
        }),
        tap(() => {
          this.isLoading = false;
        }),
        takeUntil(this.destroy$)
      ).subscribe();
    }
  }

  /**
   * Fetch the cross-collection claim/airdrop event via nft-indexer REST API.
   * - On V1 site: fetch V2 assign → displayed as "V2Airdrop"
   * - On V2 site: fetch V1 assign → displayed as "Claimed" (purple, with link to V1)
   */
  private fetchCrossCollectionEvent(tokenId: string): Observable<Event[]> {
    if (this.salesOnly) return of([]);
    if (!environment.featureFlags?.CROSS_MARKETPLACE_COUNTERPART) return of([]);

    const isV1 = environment.collection === 'cryptopunks-v1';
    const isV2 = environment.collection === 'cryptopunks';
    if (!isV1 && !isV2) return of([]);

    const crossContract = isV1
      ? this.V2_PUNKS_CONTRACT
      : environment.v1PunksContractAddress;
    if (!crossContract) return of([]);

    let baseUrl = environment.alternativeGraphURI ?? '';
    if (baseUrl.endsWith('/graphql')) {
      baseUrl = baseUrl.replace(/\/graphql$/, '');
    }
    if (!baseUrl) return of([]);

    const url = `${baseUrl}/events/token/${tokenId}`;
    const v2Mapper = this.graphqlService.getMapper('cryptopunks', 'GET_TX_HISTORY');

    return this.http.get<any[]>(url, {
      params: { collections: crossContract }
    }).pipe(
      map((events) => {
        const assignEvents = events.filter((e: any) => e.eventType === 'assign');
        const mapped: Event[] = v2Mapper
          ? v2Mapper({ getAllEvents: assignEvents })
          : assignEvents;
        const typeOverride: Event['type'] = isV1 ? 'V2Airdrop' : 'V1Claimed';
        return mapped.map(e => ({ ...e, type: typeOverride, isCrossCollection: true }));
      }),
      catchError(() => of([]))
    );
  }

  loadMore(): void {
    this.fetchEvents(true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isPunksmarketNativeSale(event: Event): boolean {
    return (
      environment.collection === 'cryptopunks-v1' && isPunksmarketNativeSale(event)
    );
  }
}
